import React, { useEffect, useMemo, useState } from 'react';
import './ProjectProgress.css';
import RoleBadges from './RoleBadges';
import { projectAPI } from '../services/api';

const stageDefinitions = [
  {
    key: 'design',
    label: '设计',
    percent: 0,
    completed: (project) => project.engineeringCompleted === true,
    completedTime: (project) => project.engineeringCompletedTime,
    completedBy: (project) => project.engineeringCompletedBy
  },
  {
    key: 'purchase',
    label: '采购',
    percent: 15,
    completed: (project) => project.purchaseCompleted === true,
    completedTime: (project) => project.purchaseCompletedTime,
    completedBy: (project) => project.purchaseCompletedBy
  },
  {
    key: 'processing',
    label: '加工',
    percent: 30,
    completed: (project) => project.processingCompleted === true,
    completedTime: (project) => project.processingCompletedTime,
    completedBy: (project) => project.processingCompletedBy
  },
  {
    key: 'materialWarehouse',
    label: '备料入出库',
    percent: 45,
    completed: (project) => project.warehouseInCompleted === true && project.warehouseOutCompleted === true,
    completedTime: (project) => project.warehouseOutCompletedTime || project.warehouseInCompletedTime,
    completedBy: (project) => project.warehouseOutCompletedBy || project.warehouseInCompletedBy
  },
  {
    key: 'assembly',
    label: '装配',
    percent: 60,
    completed: (project) => project.assemblyCompleted === true,
    completedTime: (project) => project.assemblyCompletedTime,
    completedBy: (project) => project.assemblyCompletedBy
  },
  {
    key: 'testing',
    label: '调试',
    percent: 75,
    completed: (project) => project.testingCompleted === true,
    completedTime: (project) => project.testingCompletedTime,
    completedBy: (project) => project.testingCompletedBy
  },
  {
    key: 'finalWarehouseIn',
    label: '整机入库',
    percent: 90,
    completed: (project) => project.warehouseInSecondCompleted === true || project.archived === true,
    completedTime: (project) => project.warehouseInSecondCompletedTime || project.archivedTime,
    completedBy: (project) => project.warehouseInSecondCompletedBy || project.archivedBy
  }
];

const formatDate = (value) => {
  if (!value) return '暂无';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '暂无';
  return date.toLocaleDateString('zh-CN');
};

const getPriorityText = (priority) => {
  const priorityMap = {
    urgent: '紧急',
    high: '高',
    normal: '普通',
    medium: '中',
    low: '低'
  };
  return priorityMap[priority] || '普通';
};

const buildProgress = (project) => {
  const stages = stageDefinitions.map((stage) => ({
    ...stage,
    isCompleted: stage.completed(project),
    completedTime: stage.completedTime(project),
    completedBy: stage.completedBy(project)
  }));

  const completedCount = stages.filter((stage) => stage.isCompleted).length;
  const isCompleted = stages.every((stage) => stage.isCompleted);
  const currentStage = isCompleted ? stages[stages.length - 1] : stages.find((stage) => !stage.isCompleted);
  const percent = isCompleted ? 100 : currentStage.percent;
  const previousStage = completedCount > 0 ? stages[completedCount - 1] : null;

  return {
    stages,
    completedCount,
    currentStage,
    previousStage,
    currentLabel: isCompleted ? '已入库' : currentStage.label,
    percent,
    isCompleted
  };
};

const ProjectProgress = ({
  user,
  onLogout,
  onBackToHome,
  onSwitchToScheduleManagement,
  onSwitchToArchive,
  activeRole,
  onRoleSwitch
}) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await projectAPI.getProjects({ status: 'approved' });
      setProjects(response.projects || []);
    } catch (error) {
      console.error('加载项目进度失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const progressProjects = useMemo(() => {
    return projects.map((project) => ({
      ...project,
      progress: buildProgress(project)
    }));
  }, [projects]);

  return (
    <div className="project-progress-container">
      <div className="project-progress-header">
        <div className="header-left">
          <div className="user-info">
            <div className="user-avatar">👨‍💼</div>
            <div className="user-details">
              <div className="user-name">
                {user.displayName || user.username}
                {user.isPrimaryLeader && user.primaryLeaderRoles &&
                 user.primaryLeaderRoles.includes('manager') && (
                  <span className="primary-leader-badge">（主负责人）</span>
                )}
              </div>
              <RoleBadges
                user={user}
                activeRole={activeRole}
                onRoleSwitch={onRoleSwitch}
              />
            </div>
          </div>
        </div>
        <div className="header-right">
          <button className="schedule-management-btn" onClick={onSwitchToScheduleManagement}>
            📅 周期管理
          </button>
          <button className="progress-management-btn active" type="button">
            📈 项目进度
          </button>
          <button className="archive-management-btn" onClick={onSwitchToArchive}>
            📁 归档管理
          </button>
          <button className="create-btn" onClick={onBackToHome}>
            ➕ 新建立项
          </button>
          <button className="logout-btn" onClick={onLogout}>
            🚪 退出登录
          </button>
        </div>
      </div>

      <main className="project-progress-content">
        <div className="project-progress-title-row">
          <div>
            <h1 className="page-title">项目进度</h1>
            <p className="page-subtitle">每个项目独立显示当前推进部门和大概完成百分比</p>
          </div>
          <button className="refresh-progress-btn" onClick={loadProjects} disabled={loading}>
            ↻ 刷新
          </button>
        </div>

        <div className="project-progress-summary">
          <span>项目数量：{progressProjects.length}</span>
          <span>已入库：{progressProjects.filter((project) => project.progress.isCompleted).length}</span>
        </div>

        {loading ? (
          <div className="progress-empty-state">加载中...</div>
        ) : progressProjects.length === 0 ? (
          <div className="progress-empty-state">暂无项目</div>
        ) : (
          <div className="progress-project-list">
            {progressProjects.map((project) => {
              const { progress } = project;

              return (
                <article key={project.id || project._id} className={`progress-project-card current-${progress.currentStage.key}`}>
                  <div className="progress-project-main">
                    <div>
                      <div className="progress-project-topline">
                        <span className="project-type-badge">
                          {project.projectType === 'contract' ? '合同立项' : '研发立项'}
                        </span>
                        <span className={`progress-status-pill ${progress.currentStage.key}`}>
                          当前：{progress.currentLabel}
                        </span>
                        <span className={`priority priority-${project.priority || 'normal'}`}>
                          {getPriorityText(project.priority)}
                        </span>
                      </div>
                      <h2 className="progress-project-title">{project.projectName}</h2>
                      <p className="progress-project-description">{project.description || '暂无描述'}</p>
                    </div>
                    <div className="progress-percent-box">
                      <strong>{progress.percent}%</strong>
                      <span>{progress.currentLabel}</span>
                    </div>
                  </div>

                  <div className="single-project-progress-row">
                    <div className="progress-bar" aria-label={`${project.projectName}进度${progress.percent}%`}>
                      <div className="progress-bar-fill" style={{ width: `${progress.percent}%` }} />
                    </div>
                    <div className="current-department-label">
                      {progress.currentLabel} · {progress.percent}%
                    </div>
                  </div>

                  <div className="stage-track">
                    {progress.stages.map((stage) => (
                      <div
                        key={stage.key}
                        className={`stage-node ${stage.isCompleted ? 'completed' : ''} ${stage.key === progress.currentStage.key && !progress.isCompleted ? 'current' : ''}`}
                        title={`${stage.label}：${stage.isCompleted ? `已完成，${formatDate(stage.completedTime)}` : stage.key === progress.currentStage.key ? '当前阶段' : '未开始'}`}
                      >
                        <span className="stage-dot" />
                        <span className="stage-label">{stage.label}</span>
                      </div>
                    ))}
                  </div>

                  <div className="progress-project-meta">
                    <span>当前部门：{progress.currentLabel}</span>
                    <span>申请人：{project.createdByName || '未知'}</span>
                    <span>上一阶段完成：{progress.previousStage ? `${progress.previousStage.label} ${formatDate(progress.previousStage.completedTime)}` : '暂无'}</span>
                    {progress.previousStage?.completedBy && <span>上一阶段负责人：{progress.previousStage.completedBy}</span>}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default ProjectProgress;
